import type { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import initApi from '@/api/initApiV2';
import type { RequestConfig } from '@/api/Request.ts';
import {
  AiUsageApi,
  type AIModelPrice,
  type AIUsageEventPage,
  type AIUsageOrganizationOverview,
  type AIUsageOverview,
  type AiUsageApiGetAiUsageOverviewRequest,
  type AiUsageApiGetOrganizationAiUsageOverviewRequest,
  type AiUsageApiListAiUsageEventsRequest,
} from '@/clients/v2';
import { handleApiError } from '@/api/handleApiError.ts';

export type AIUsageEventFilters = Omit<
  AiUsageApiListAiUsageEventsRequest,
  'page' | 'size' | 'sortBy' | 'descending'
>;

export const useAIUsageMetricsStore = defineStore('ai-usage-metrics', () => {
  const overview = ref<AIUsageOverview | null>(null);
  const organizationOverview = ref<AIUsageOrganizationOverview | null>(null);
  const eventsPage = ref<AIUsageEventPage | null>(null);
  const modelPrices = ref<AIModelPrice[] | null>(null);

  async function getOverview(params: AiUsageApiGetAiUsageOverviewRequest) {
    overview.value = null;

    try {
      const api = await initApi(AiUsageApi);

      if (!api) return;

      const response = await api.getAiUsageOverview(params);
      overview.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function getEvents(params: AiUsageApiListAiUsageEventsRequest, options?: RequestConfig) {
    try {
      const api = await initApi(AiUsageApi);

      if (!api) return;

      const response = await api.listAiUsageEvents(params, options);
      eventsPage.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function getOrganizationOverview(params: AiUsageApiGetOrganizationAiUsageOverviewRequest) {
    organizationOverview.value = null;

    try {
      const api = await initApi(AiUsageApi);

      if (!api) return;

      const response = await api.getOrganizationAiUsageOverview(params);
      organizationOverview.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  async function getModelPrices() {
    modelPrices.value = null;

    try {
      const api = await initApi(AiUsageApi);

      if (!api) return;

      const response = await api.listAiModelPrices();
      modelPrices.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  }

  return {
    overview,
    organizationOverview,
    eventsPage,
    modelPrices,
    getOverview,
    getOrganizationOverview,
    getEvents,
    getModelPrices,
  };
});
